import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, TextField } from "@mui/material";
import { useState } from "react";

import { useGlobalModal } from "../../../components/Contexts/GlobalModalContext";
import { ModalActions } from "../../../components/Modals/ModalActions";
import { Button } from "../../../components/Buttons/Button";
import { DiggingLocationDTO } from "../../../DTOs/Production/DiggingLocationDTO";

type MoveLoadingRecordModalProps = {
  availableLocations: DiggingLocationDTO[];
  currentLocation: DiggingLocationDTO;
  onMove: (newDiggingPatternId: number) => Promise<boolean>;
}

const MoveLoadingRecordModal: React.FC<MoveLoadingRecordModalProps> = ({
  availableLocations,
  currentLocation,
  onMove
}) => {
  const { hideModal } = useGlobalModal();
  const [isWorking, setIsWorking] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number>(0);

  return (
    <form>
      <h2 className="border-b border-theme-border font-semibold pb-2 text-[1.6rem] text-theme-text">
        Move Loading Record
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className={`text-theme-text text-sm transition ease-out mb-2 ml-1 opacity-${isWorking ? 100 : 0}`}
        />
      </h2>
      <div className="pt-2 mb-2">
        <p className="text-sm text-theme-text mb-1"><span className="font-semibold">From: </span>Blastmaster <span className="font-semibold">{currentLocation.blastMasterId ?? currentLocation.patternBlastId}</span> ({currentLocation.pit})</p>
      </div>

      <div className="flex flex-col gap-1 mb-4">
        <p className="text-sm text-theme-text"><span className="font-semibold">To: </span></p>
        <div className="flex flex-col">
          <Autocomplete<DiggingLocationDTO, false, false, false>
            classes={{ root: "w-full" }}
            size="small"
            options={availableLocations}
            value={availableLocations.find(o => o.diggingPatternId === selectedLocationId) ?? null}
            getOptionLabel={(o) => `Blastmaster: ${o.blastMasterId ?? o.patternBlastId} - Pit: ${o.pit}`}
            renderOption={(props, o) => {
              return (
                <li {...props} key={o.diggingPatternId} className="p-2 cursor-pointer hover:bg-theme-surface transition duration-150">
                  Blastmaster: <span className="font-semibold">{o.blastMasterId ?? o.patternBlastId}</span> - Pit: <span className="font-semibold">{o.pit}</span>
                </li>
              )
            }}
            isOptionEqualToValue={(opt, val) => opt.diggingPatternId === val.diggingPatternId}
            onChange={(_, opt) => setSelectedLocationId(opt?.diggingPatternId ?? 0)}
            noOptionsText="No other locations available"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select blastmaster"
                size="small"
              />
            )}
          />
        </div>
      </div>

      <ModalActions className="!mt-12">
        <Button variant="muted" className="!text-theme-text" onClick={hideModal}>
          Cancel
        </Button>
        <Button
          variant="info"
          isDefaultAction
          mnemonic="m"
          disabled={selectedLocationId === 0}
          onClick={async () => {
            if (selectedLocationId === 0) {
              return;
            }
            setIsWorking(true);
            const success = await onMove(selectedLocationId);
            if (success) {
              hideModal();
            }
            setIsWorking(false);
          }}
        >
          Move
        </Button>
      </ModalActions>
    </form>
  );
};

export default MoveLoadingRecordModal;